import { useState, useMemo } from "react";
import {
  Chat24Regular,
  DocumentBulletList24Regular,
  DataBarVertical24Regular,
  Shield24Regular,
  Headset24Regular,
  ArrowTrending24Regular,
  Money24Regular,
  DocumentText24Regular,
  Settings24Regular,
  ArrowUpRight20Regular,
  LockClosed20Regular,
  Clock20Regular,
  Search20Regular,
  Briefcase24Regular,
  Sparkle24Regular,
  Bot24Regular,
} from "@fluentui/react-icons";
import { useShell, ShellAgent, PKB_AGENT_ID } from "../../context/ShellContext";
import { useNotifications } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import "./AgentsStore.css";

const agentIconMap: Record<string, React.ReactElement> = {
  Chat: <Chat24Regular />,
  DocumentBulletList: <DocumentBulletList24Regular />,
  Briefcase: <Briefcase24Regular />,
  ClipboardTask: <Sparkle24Regular />,
  DataBarVertical: <DataBarVertical24Regular />,
  Shield: <Shield24Regular />,
  Headset: <Headset24Regular />,
  TrendingUp: <ArrowTrending24Regular />,
  Money: <Money24Regular />,
  DocumentText: <DocumentText24Regular />,
  Settings: <Settings24Regular />,
};

export function AgentsStore() {
  const { agents, setActiveAgentById, isAdmin } = useShell();
  const { myRequestStatuses, requestAccess } = useNotifications();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [requestingAgent, setRequestingAgent] = useState<string | null>(null);

  const categories = ["All", "RFP", "Research", "Products", "Security", "Support", "Market", "Finance"];

  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      // PKB is the home page — not listed as an agent in the store
      if (agent.agentId === PKB_AGENT_ID) return false;

      const matchesSearch =
        !searchQuery ||
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory =
        activeCategory === "All" ||
        agent.tags.some((t) => t.toLowerCase() === activeCategory.toLowerCase());

      return matchesSearch && matchesCategory;
    });
  }, [agents, searchQuery, activeCategory]);

  const handleAgentClick = (agent: ShellAgent) => {
    if (!agent.hasAccess) return;
    setActiveAgentById(agent.agentId);
    navigate(`/agent/${agent.route}`);
  };

  const handleRequestAccess = async (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    setRequestingAgent(agentId);
    try {
      await requestAccess(agentId);
    } catch (err) {
      console.error("Failed to request access:", err);
    } finally {
      setRequestingAgent(null);
    }
  };

  return (
    <div className="agents-store">
      <div className="agents-store-header">
        <div className="agents-store-header-left">
          <h1 className="agents-store-title">Agent Store</h1>
          <p className="agents-store-subtitle">
            Discover and install AI agents to power your retail workflows
          </p>
        </div>
        <span className="agents-count-badge">{agents.filter(a => a.agentId !== PKB_AGENT_ID).length} agents</span>
      </div>

      <div className="agents-store-search">
        <Search20Regular className="search-icon" />
        <input
          type="text"
          className="search-input"
          placeholder="Search by name, tag or capability..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="agents-store-categories">
        {categories.map((cat) => (
          <button
            key={cat}
            className={`category-pill ${activeCategory === cat ? "active" : ""}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="agents-store-grid">
        {filteredAgents.map((agent) => (
          <div
            key={agent.agentId}
            className={`agent-card ${agent.hasAccess ? "" : "no-access"}`}
            onClick={() => handleAgentClick(agent)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleAgentClick(agent);
            }}
          >
            <div className="agent-card-top">
              <span
                className="agent-card-icon"
                style={{ backgroundColor: `${agent.iconColor}14`, color: agent.iconColor }}
              >
                {agentIconMap[agent.iconName] ?? <Chat24Regular />}
              </span>
              {agent.hasAccess ? (
                <button
                  className="agent-card-action"
                  title="Launch Agent"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAgentClick(agent);
                  }}
                >
                  <ArrowUpRight20Regular />
                  <span>Launch Agent</span>
                </button>
              ) : myRequestStatuses.get(agent.agentId) === "Pending" || requestingAgent === agent.agentId ? (
                <button
                  className="agent-card-action pending"
                  title="Access request pending"
                  disabled
                >
                  <Clock20Regular />
                  <span>Pending</span>
                </button>
              ) : (
                <button
                  className="agent-card-action request-access"
                  title="Request access to this agent"
                  disabled={requestingAgent !== null}
                  onClick={(e) => handleRequestAccess(e, agent.agentId)}
                >
                  <LockClosed20Regular />
                  <span>Request Access</span>
                </button>
              )}
            </div>
            <h3 className="agent-card-name">{agent.name}</h3>
            <p className="agent-card-publisher">{agent.publisher}</p>
            <p className="agent-card-desc">{agent.description}</p>
            <div className="agent-card-tags">
              {agent.tags.map((tag) => (
                <span key={tag} className="agent-tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
